import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Table, Button, Upload, message, Progress, Alert, Space, Checkbox } from 'antd';
import {
  ExportOutlined,
  ImportOutlined,
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import templateService from 'services/templateService';

/**
 * ImportExport - Component for bulk template import/export operations
 */
const ImportExport = ({ visible, onClose, templates, onImportSuccess }) => {
  const [mode, setMode] = useState('export'); // 'export' or 'import'
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [importResults, setImportResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleExport = async () => {
    if (selectedTemplateIds.length === 0) {
      message.warning('Please select at least one template to export');
      return;
    }

    try {
      setExporting(true);
      setProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await templateService.exportTemplates(selectedTemplateIds);

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        // Create JSON file and download
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `templates_export_${Date.now()}.json`;
        link.click();

        URL.revokeObjectURL(url);

        message.success(`${selectedTemplateIds.length} templates exported successfully`);
        setSelectedTemplateIds([]);
      }
    } catch (error) {
      message.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
      setProgress(0);
    }
  };

  const handleImport = async (file) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        setImporting(true);
        setProgress(0);

        // Parse JSON
        const templates = JSON.parse(e.target.result);

        // Validate structure
        if (!Array.isArray(templates)) {
          throw new Error('Invalid file format. Expected an array of templates.');
        }

        // Validate each template
        const validationErrors = [];
        templates.forEach((template, index) => {
          if (!template.templateName) {
            validationErrors.push(`Template ${index + 1}: Missing template name`);
          }
          if (!template.analysisLevel) {
            validationErrors.push(`Template ${index + 1}: Missing analysis level`);
          }
        });

        if (validationErrors.length > 0) {
          message.error({
            content: (
              <div>
                <p>Validation errors found:</p>
                <ul>
                  {validationErrors.slice(0, 5).map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
                {validationErrors.length > 5 && <p>...and {validationErrors.length - 5} more</p>}
              </div>
            ),
            duration: 10,
          });
          setImporting(false);
          return;
        }

        // Import templates
        const results = [];
        const total = templates.length;

        for (let i = 0; i < templates.length; i++) {
          try {
            setProgress(Math.round(((i + 1) / total) * 100));

            const response = await templateService.createTemplate(templates[i]);

            results.push({
              templateName: templates[i].templateName,
              status: 'success',
              message: 'Imported successfully',
            });
          } catch (error) {
            results.push({
              templateName: templates[i].templateName,
              status: 'error',
              message: error.message,
            });
          }
        }

        setImportResults(results);

        const successCount = results.filter((r) => r.status === 'success').length;
        const errorCount = results.filter((r) => r.status === 'error').length;

        if (errorCount === 0) {
          message.success(`All ${successCount} templates imported successfully`);
          onImportSuccess?.();
        } else {
          message.warning(`${successCount} succeeded, ${errorCount} failed. Check details below.`);
        }
      } catch (error) {
        message.error('Import failed: ' + error.message);
      } finally {
        setImporting(false);
        setProgress(0);
      }
    };

    reader.onerror = () => {
      message.error('Failed to read file');
      setImporting(false);
    };

    reader.readAsText(file);
    return false; // Prevent auto upload
  };

  const exportColumns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
    },
    {
      title: 'Analysis Level',
      dataIndex: 'analysisLevel',
      key: 'analysisLevel',
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
  ];

  const importResultColumns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 'success' ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
        ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedTemplateIds,
    onChange: (selectedRowKeys) => {
      setSelectedTemplateIds(selectedRowKeys);
    },
  };

  return (
    <StyledModal
      title="Import/Export Templates"
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Container>
        {/* Mode Selector */}
        <ModeSelector>
          <Button
            type={mode === 'export' ? 'primary' : 'default'}
            icon={<ExportOutlined />}
            onClick={() => setMode('export')}
            size="large"
          >
            Export Templates
          </Button>
          <Button
            type={mode === 'import' ? 'primary' : 'default'}
            icon={<ImportOutlined />}
            onClick={() => setMode('import')}
            size="large"
          >
            Import Templates
          </Button>
        </ModeSelector>

        {/* Export Mode */}
        {mode === 'export' && (
          <ExportSection>
            <Alert
              message="Export Templates"
              description="Select templates to export as JSON. You can import them later or share with other environments."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <ActionBar>
              <Space>
                <Checkbox
                  checked={selectedTemplateIds.length === templates.length}
                  indeterminate={
                    selectedTemplateIds.length > 0 &&
                    selectedTemplateIds.length < templates.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTemplateIds(templates.map((t) => t.templateId));
                    } else {
                      setSelectedTemplateIds([]);
                    }
                  }}
                >
                  Select All ({templates.length} templates)
                </Checkbox>
              </Space>

              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={selectedTemplateIds.length === 0}
                loading={exporting}
              >
                Export Selected ({selectedTemplateIds.length})
              </Button>
            </ActionBar>

            {exporting && progress > 0 && (
              <Progress percent={progress} status="active" style={{ marginBottom: 16 }} />
            )}

            <Table
              rowSelection={rowSelection}
              columns={exportColumns}
              dataSource={templates}
              rowKey="templateId"
              pagination={{ pageSize: 10 }}
            />
          </ExportSection>
        )}

        {/* Import Mode */}
        {mode === 'import' && (
          <ImportSection>
            <Alert
              message="Import Templates"
              description="Upload a JSON file containing templates exported from this or another system. Templates will be created as new entries."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <UploadArea>
              <Upload.Dragger
                accept=".json"
                beforeUpload={handleImport}
                showUploadList={false}
                disabled={importing}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">Click or drag JSON file to this area to upload</p>
                <p className="ant-upload-hint">
                  Only .json files exported from this system are supported
                </p>
              </Upload.Dragger>
            </UploadArea>

            {importing && progress > 0 && (
              <Progress
                percent={progress}
                status="active"
                style={{ marginTop: 16 }}
                format={(percent) => `Importing... ${percent}%`}
              />
            )}

            {importResults.length > 0 && (
              <ResultsSection>
                <h3>Import Results</h3>
                <Table
                  columns={importResultColumns}
                  dataSource={importResults}
                  rowKey={(record, index) => index}
                  pagination={{ pageSize: 10 }}
                />
              </ResultsSection>
            )}
          </ImportSection>
        )}
      </Container>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const Container = styled.div``;

const ModeSelector = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  justify-content: center;
`;

const ExportSection = styled.div``;

const ImportSection = styled.div``;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
`;

const UploadArea = styled.div`
  .ant-upload-drag {
    padding: 40px;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;

  h3 {
    margin-bottom: 16px;
  }
`;

export default ImportExport;
